import { defineStore } from 'pinia';
import { watch } from 'vue';

import { dashboardBffClient } from '@/api-client';
import { useApiRequest } from '@/composables/useApiRequest';
import { useUserStore } from '@/stores/user';

export type AccessRightParams = Parameters<
  typeof dashboardBffClient.bff.getUserPermissionFlagForResource
>[0];

export const useAccessRightsStore = defineStore('accessRights', () => {
  const userStore = useUserStore();
  const accessRightsApi = useApiRequest(dashboardBffClient.bff.getUserPermissionFlagForResource, {
    transformResponse: (response) => response.data.allowed,
    cacheTimeout: 2 * 60 * 60 * 1000,
    cacheKey: ({ subject, verb }) => `${verb}/${subject}`,
  });

  const resetAll = () => accessRightsApi.resetCache();

  function isAccessRightPending(
    verb: AccessRightParams['verb'],
    subject: AccessRightParams['subject'],
  ) {
    // Schedule request to make it outside synchronous computed context.
    // For some reason if you do manipulations that lead of ref mutations inside computed it breaks reactivity.
    // Request by itself starts request and mutates a few things for that.
    setTimeout(() => accessRightsApi.request({ subject, verb }));
    return accessRightsApi.getIsPendingByArgs({ subject, verb }) ?? true;
  }

  function allowed(verb: AccessRightParams['verb'], subject: AccessRightParams['subject']) {
    // Schedule request to make it outside synchronous computed context.
    // For some reason if you do manipulations that lead of ref mutations inside computed it breaks reactivity.
    // Request by itself starts request and mutates a few things for that.
    setTimeout(() => accessRightsApi.request({ subject, verb }));
    return accessRightsApi.getResponseByArgs({ subject, verb }) ?? false;
  }

  function denied(verb: AccessRightParams['verb'], subject: AccessRightParams['subject']) {
    // Schedule request to make it outside synchronous computed context.
    // For some reason if you do manipulations that lead of ref mutations inside computed it breaks reactivity.
    // Request by itself starts request and mutates a few things for that.
    setTimeout(() => accessRightsApi.request({ subject, verb }));
    const isAllowed = accessRightsApi.getResponseByArgs({ subject, verb });
    return isAllowed === undefined ? false : !isAllowed;
  }

  async function waitForAccessRight(
    verb: AccessRightParams['verb'],
    subject: AccessRightParams['subject'],
  ) {
    await accessRightsApi.request({ subject, verb });
    return allowed(verb, subject);
  }

  function isAdminAccessRightPending() {
    return isAccessRightPending('list', 'restorepointcontents');
  }

  function isAdmin() {
    return allowed('list', 'restorepointcontents');
  }

  async function waitForIsAdmin() {
    return waitForAccessRight('list', 'restorepointcontents');
  }

  watch(
    () => userStore.authUser,
    (newUser) => {
      if (newUser === undefined) resetAll();
    },
  );

  return {
    waitForAccessRight,
    isAccessRightPending,
    allowed,
    denied,
    waitForIsAdmin,
    isAdminAccessRightPending,
    isAdmin,
    resetAll,
  };
});
